import { parsePaymentAmount, sanitizePaymentAmount } from "../util/sanitizePaymentAmount";
import { formatCurrency } from "./util/formatCurrency";

// Prefix match covers both renders of the form's x-data (legacy
// `paymentCardForm` and the BT-enabled `paymentCardForm({...})`).
// Exact-match silently misses the BT render.
const FORM_SELECTOR = 'form[x-data^="paymentCardForm"]';
const ACH_FORM_SELECTOR = 'form[x-data^="paymentAchForm"]';
const SURCHARGE_DEBOUNCE_MS = 500;

export default function paymentSurcharge({
    contractId,
    baseAmount,
    selected = 'bank_account',
    email = '',
    isPaymentLink = false,
    ownerId = null
}) {
    return {
        selected,
        email,
        surcharge: '0.00',
        isLoadingSurcharge: false,
        surchargeTimeout: null,
        surchargeAbortController: null,
        paymentAmount: parseFloat(baseAmount),

        get contractId() { return contractId; },
        get baseAmount() { return baseAmount; },

        get displayTotal() {
            if (this.selected === 'credit_card' && parseFloat(this.surcharge) > 0) {
                return this.paymentAmount + parseFloat(this.surcharge);
            }
            return this.paymentAmount;
        },

        readCurrentAmount() {
            const form = document.querySelector(FORM_SELECTOR);
            const amountInput = form ? form.querySelector('input[inputmode="decimal"]') : null;
            const amountValue = amountInput ? amountInput.value.trim() : '';
            const parsed = parsePaymentAmount(amountValue);
            if (isNaN(parsed) || parsed <= 0) {
                return parseFloat(baseAmount);
            }
            return parsed;
        },

        updateTotalToPayField(total) {
            const form = document.querySelector(FORM_SELECTOR);
            const totalToPayInput = form ? form.querySelector('input[name*="totalToPay"]') : null;
            if (totalToPayInput && this.selected === 'credit_card') {
                totalToPayInput.value = total.toFixed(2);
            }
        },

        updateTotalToPayForCurrentAmount() {
            if (this.selected === 'credit_card') {
                const total = this.readCurrentAmount() + parseFloat(this.surcharge);
                this.updateTotalToPayField(total);
            }
        },

        formatCurrency,

        syncPaymentAmount() {
            const formSelector = this.selected === 'credit_card'
                ? FORM_SELECTOR
                : ACH_FORM_SELECTOR;
            const form = document.querySelector(formSelector);
            const amountInput = form ? form.querySelector('input[inputmode="decimal"]') : null;
            const val = amountInput ? parsePaymentAmount(amountInput.value.trim()) : NaN;
            this.paymentAmount = (!isNaN(val) && val > 0) ? val : parseFloat(baseAmount);
        },

        init() {
            this.$watch('selected', (newValue) => {
                if (newValue === 'credit_card') {
                    this.fetchSurcharge();
                } else {
                    this.resetSurcharge();
                }
                this.syncPaymentAmount();
            });

            this.watchRelevantFields();
            this.watchAmountInputs();

            window.addEventListener('pageshow', (e) => {
                if (!e.persisted) return;
                this.syncPaymentAmount();
            });
        },

        watchRelevantFields() {
            const form = document.querySelector(FORM_SELECTOR);
            if (!form) return;

            // BT path: x-model writes the token intent id into the
            // hidden `card_token` input and the form component
            // dispatches an `input` event after tokenIntents.create
            // resolves. Legacy path: this input isn't rendered.
            // _surchargeWatched guards against listener stacking if
            // init runs twice (bfcache).
            const cardTokenInput = form.querySelector('input[name="card_token"]');
            if (cardTokenInput && !cardTokenInput._surchargeWatched) {
                cardTokenInput._surchargeWatched = true;
                cardTokenInput.addEventListener('input', () => this.onRelevantFieldChange());
            }

            // Reset surcharge immediately on card number change to avoid
            // stale values from the previous card while the new fetch is in flight.
            const cardNumberInput = form.querySelector('input[autocomplete="cc-number"]');
            if (cardNumberInput && !cardNumberInput._surchargeWatched) {
                cardNumberInput._surchargeWatched = true;
                cardNumberInput.addEventListener('input', () => {
                    this.resetSurcharge();
                    this.onRelevantFieldChange();
                });
            }

            const amountInput = form.querySelector('input[inputmode="decimal"]');
            if (amountInput && !amountInput._surchargeWatched) {
                amountInput._surchargeWatched = true;
                amountInput.addEventListener('input', () => this.onRelevantFieldChange());
            }
        },

        async fetchSurcharge() {
            const form = document.querySelector(FORM_SELECTOR);
            if (!form) {
                return;
            }

            // Prefer the BT token (flag-on path) over the raw PAN
            // (legacy path); one of the two inputs is rendered.
            const cardToken = form.querySelector('input[name="card_token"]')?.value ?? '';
            const cardNumber = form.querySelector('input[autocomplete="cc-number"]')?.value ?? '';
            const amountInput = form.querySelector('input[inputmode="decimal"]');
            const rawAmount = amountInput ? amountInput.value : null;
            const amount = parsePaymentAmount(rawAmount);

            if ((!cardToken && !cardNumber) || !rawAmount || isNaN(amount) || amount <= 0) {
                this.resetSurcharge();
                return;
            }

            // Cancel any in-flight surcharge call so a late response
            // from a prior card/amount can't overwrite the current one.
            this.surchargeAbortController?.abort();
            this.surchargeAbortController = new AbortController();

            this.isLoadingSurcharge = true;

            try {
                const actualFormData = new FormData(form);
                const surchargeFormData = new FormData();
                for (const [key, value] of actualFormData.entries()) {
                    if (key.includes('emailAddress')) {
                        continue;
                    }
                    if (key.includes('[amount]')) {
                        surchargeFormData.append(key, sanitizePaymentAmount(value));
                    } else {
                        surchargeFormData.append(key, value);
                    }
                }

                const endpoint = isPaymentLink
                    ? `/pay/surcharge?o=${ownerId}&c=${this.contractId}`
                    : `/clubhouse/payments/${this.contractId}/surcharge`;

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: surchargeFormData,
                    signal: this.surchargeAbortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                this.surcharge = data.surcharge || '0.00';

                this.updateTotalToPayForCurrentAmount();
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('Failed to fetch surcharge:', error);
                this.resetSurcharge();
            } finally {
                this.isLoadingSurcharge = false;
            }
        },

        resetSurcharge() {
            clearTimeout(this.surchargeTimeout);
            this.surchargeTimeout = null;
            this.surchargeAbortController?.abort();
            this.surchargeAbortController = null;
            this.surcharge = '0.00';
            this.updateTotalToPayForCurrentAmount();
        },

        onRelevantFieldChange() {
            if (this.selected !== 'credit_card') {
                return;
            }
            clearTimeout(this.surchargeTimeout);
            this.surchargeTimeout = setTimeout(() => this.fetchSurcharge(), SURCHARGE_DEBOUNCE_MS);
        },

        watchAmountInputs() {
            const achForm = document.querySelector(ACH_FORM_SELECTOR);
            if (achForm) {
                const achAmountInput = achForm.querySelector('input[inputmode="decimal"]');
                if (achAmountInput) {
                    achAmountInput.addEventListener('input', () => {
                        if (this.selected === 'bank_account') {
                            const val = parsePaymentAmount(achAmountInput.value.trim());
                            this.paymentAmount = (!isNaN(val) && val > 0) ? val : parseFloat(baseAmount);
                        }
                    });
                }
            }

            const ccForm = document.querySelector(FORM_SELECTOR);
            if (ccForm) {
                const ccAmountInput = ccForm.querySelector('input[inputmode="decimal"]');
                if (ccAmountInput) {
                    ccAmountInput.addEventListener('input', () => {
                        if (this.selected === 'credit_card') {
                            const val = parsePaymentAmount(ccAmountInput.value.trim());
                            this.paymentAmount = (!isNaN(val) && val > 0) ? val : parseFloat(baseAmount);
                            this.updateTotalToPayForCurrentAmount();
                        }
                    });
                }
            }
        }
    };
}
